// The player's city tags for a world (src/game/tagbook.ts), read from and saved to the Worker's KV store. One query per
// world; a save puts the stored book straight into the cache.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { cleanBook, emptyBook, type TagBook } from "@/game/tagbook";
import { usePrompts } from "./prompts";

export const tagKeys = { book: (wid: number) => ["tags", wid] as const };

export function useTagBook(wid: number) {
  return useQuery({
    queryKey: tagKeys.book(wid),
    queryFn: async () => cleanBook(await api.tags.get(wid)),
    enabled: wid > 0,
    staleTime: 60_000,
    retry: 1,
    placeholderData: emptyBook,
  });
}

export function useSaveTags(wid: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (book: TagBook) => api.tags.save(wid, cleanBook(book)),
    onSuccess: (saved) => {
      qc.setQueryData(tagKeys.book(wid), cleanBook(saved));
      usePrompts.getState().toast("Tags saved.", "success");
    },
    onError: (e) => usePrompts.getState().error(e),
  });
}
